import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { CustomerPipelineFilters } from '../types'
import { Button, Typogaphy } from 'vnetwork-ui'
import { useMemo, useRef, useState } from 'react'
import { TrendingUp, TrendingDown, Minus, Download } from 'lucide-react'
import { SCHEMES } from '@/components/EchartCustomized'
import EmployeeContactsTable from './EmployeeContactsTable'
import { useDailyInputData } from '../hooks/use-daily-input-data'
import {
    formatXAxis,
    formatTooltipLabel,
    exportChartToPDF,
} from '../utils/daily-input-chart-utils'

interface DailyInputChartProps {
    viewTable?: boolean
    className?: string
    filters: CustomerPipelineFilters
}

const GrowthIndicator = ({ rate }: { rate: number | null }) => {
    if (rate === null) return null
    if (rate > 0)
        return (
            <div className="flex items-center justify-end gap-1 text-green-600">
                <TrendingUp size={16} />
                <span className="text-sm font-medium">+{rate.toFixed(1)}%</span>
            </div>
        )
    if (rate < 0)
        return (
            <div className="flex items-center justify-end gap-1 text-red-600">
                <TrendingDown size={16} />
                <span className="text-sm font-medium">{rate.toFixed(1)}%</span>
            </div>
        )
    return (
        <div className="flex items-center justify-end gap-1 text-gray-500">
            <Minus size={16} />
            <span className="text-sm font-medium">0%</span>
        </div>
    )
}

const TOOLTIP_STYLE = {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
}

const DailyInputChart: React.FC<DailyInputChartProps> = ({
    filters,
    viewTable = true,
    className,
}) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)

    const {
        chartData,
        employeeData,
        previousEmployeeData,
        totalContacts,
        previousTotalContacts,
        growthRate,
        avgValue,
        granularity,
        renderState,
    } = useDailyInputData(filters)

    const avgLabel = useMemo(() => {
        const labels: Record<string, string> = {
            week: t('avg_per_week'),
            month: t('avg_per_month'),
            day: t('avg_per_day'),
        }
        return labels[granularity] ?? labels.day
    }, [granularity, t])

    const handleExportPDF = async () => {
        if (!chartRef.current || isGeneratingPDF) return
        setIsGeneratingPDF(true)
        try {
            const name =
                employeeData.length === 1 ? employeeData[0].employee_name : null
            await exportChartToPDF(chartRef.current, name, filters)
        } catch (error) {
            console.error('Error generating PDF:', error)
        } finally {
            setIsGeneratingPDF(false)
        }
    }

    const tooltipFormatter = (value: number | undefined) => [
        (value ?? 0).toLocaleString(),
        t('new_contacts'),
    ]

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div
            ref={chartRef}
            className={`bg-white rounded-md p-4 shadow-sm border border-gray-100 h-full flex flex-col ${className}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <div>
                    <Typogaphy className="font-semibold text-primary capitalize">
                        {t('daily_customer_input')}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500">
                        {t('new_contacts_entered_daily')}
                    </Typogaphy>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex gap-4">
                        <div className="text-right">
                            <div className="flex items-center gap-2 justify-end">
                                <Typogaphy className="font-medium text-primary">
                                    {totalContacts.toLocaleString()}
                                </Typogaphy>
                                <GrowthIndicator rate={growthRate} />
                            </div>
                            <Typogaphy className="text-xs text-gray-500">
                                {t('total_contacts')}
                                {previousTotalContacts > 0 && (
                                    <span className="ml-1 text-gray-400">
                                        ({t('previous')}:{' '}
                                        {previousTotalContacts.toLocaleString()}
                                        )
                                    </span>
                                )}
                            </Typogaphy>
                        </div>
                        <div className="text-right">
                            <Typogaphy className="font-medium text-emerald-600">
                                {avgValue}
                            </Typogaphy>
                            <Typogaphy className="text-xs text-gray-500">
                                {avgLabel}
                            </Typogaphy>
                        </div>
                    </div>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleExportPDF}
                        disabled={isGeneratingPDF}
                        loading={isGeneratingPDF}
                        className="!min-w-0 !px-2"
                    >
                        {!isGeneratingPDF && <Download size={16} />}
                    </Button>
                </div>
            </div>

            {/* Chart */}
            {chartData.length === 0 ? (
                <div className="flex justify-center items-center flex-1 text-gray-500">
                    {t('no_data_available')}
                </div>
            ) : (
                <div className="flex-1 flex flex-col">
                    <ResponsiveContainer width="100%" height="100%">
                        {granularity === 'day' ? (
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient
                                        id="colorContacts"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor={SCHEMES.info[0]}
                                            stopOpacity={0.3}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor={SCHEMES.info[0]}
                                            stopOpacity={0}
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#e5e7eb"
                                />
                                <XAxis
                                    dataKey="key"
                                    tickFormatter={(v) =>
                                        formatXAxis(v, granularity)
                                    }
                                    stroke="#9ca3af"
                                    fontSize={12}
                                />
                                <YAxis stroke="#9ca3af" fontSize={12} />
                                <Tooltip
                                    labelFormatter={(v) =>
                                        formatTooltipLabel(v, granularity)
                                    }
                                    formatter={tooltipFormatter}
                                    contentStyle={TOOLTIP_STYLE}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="new_customers_count"
                                    stroke={SCHEMES.info[0]}
                                    strokeWidth={2}
                                    fill="url(#colorContacts)"
                                    name={t('new_contacts')}
                                />
                            </AreaChart>
                        ) : (
                            <BarChart data={chartData}>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="#e5e7eb"
                                />
                                <XAxis
                                    dataKey="key"
                                    tickFormatter={(v) =>
                                        formatXAxis(v, granularity)
                                    }
                                    stroke="#9ca3af"
                                    fontSize={12}
                                />
                                <YAxis stroke="#9ca3af" fontSize={12} />
                                <Tooltip
                                    labelFormatter={(v) =>
                                        formatTooltipLabel(v, granularity)
                                    }
                                    formatter={tooltipFormatter}
                                    contentStyle={TOOLTIP_STYLE}
                                />
                                <Bar
                                    dataKey="new_customers_count"
                                    fill={SCHEMES.info[0]}
                                    radius={[4, 4, 0, 0]}
                                    name={t('new_contacts')}
                                />
                            </BarChart>
                        )}
                    </ResponsiveContainer>

                    {viewTable && employeeData.length > 0 && (
                        <EmployeeContactsTable
                            data={employeeData}
                            previousData={previousEmployeeData}
                            totalContacts={totalContacts}
                            previousTotalContacts={previousTotalContacts}
                            className="mt-4 pt-4 border-t border-gray-100"
                        />
                    )}
                </div>
            )}
        </div>
    )
}

export default DailyInputChart
